import { Link, useParams } from "@/lib/router-compat";
import FallbackTitle from "@/components/FallbackTitle";
import { jsonLd } from "@/lib/seoHelpers";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { format } from "date-fns";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NotFound from "./NotFound";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Award, Calendar, CheckCircle2, Clock, Globe, MapPin, ShieldCheck, User, Wrench } from "lucide-react";

interface Author {
  id: string;
  name: string;
  slug: string;
  role: string | null;
  bio: string | null;
  avatar_url: string | null;
  website_url: string | null;
  social_links: Record<string, string> | null;
  meta_title: string | null;
  meta_description: string | null;
  job_title: string | null;
  years_experience: number | null;
  credentials: string[] | null;
  expertise: string[] | null;
  testing_methodology: string | null;
  location: string | null;
}

type AuthorArticle = Pick<Tables<"articles">, "id" | "title" | "slug" | "excerpt" | "featured_image" | "featured_image_alt" | "category" | "published_at" | "created_at"> & { readingTime: string };

const estimateReadTime = (content: string | null) => {
  if (!content) return "5 min read";
  const minutes = Math.ceil(content.replace(/<[^>]*>/g, "").split(/\s+/).length / 200);
  return `${minutes} min read`;
};

export async function fetchAuthorProfile(slug: string) {
      const { data: author, error: authorError } = await supabase
        .from("authors" as never)
        .select("*")
        .eq("slug", slug)
        .eq("is_active", true)
        .maybeSingle();
      if (authorError) throw authorError;
      if (!author) return null;
      const typedAuthor = author as unknown as Author;

      const { data: articles, error: articlesError } = await supabase
        .from("articles")
        .select("id,title,slug,excerpt,featured_image,featured_image_alt,category,content,published_at,created_at")
        .eq("author_id", typedAuthor.id)
        .eq("status", "published")
        .order("published_at", { ascending: false });
      if (articlesError) throw articlesError;

      // Full bodies are needed only to calculate card reading times. Never keep
      // them in the query cache/loader result, which is serialized into the HTML.
      const articleCards: AuthorArticle[] = (articles || []).map(({ content, ...article }) => ({
        ...article,
        readingTime: estimateReadTime(content),
      }));
      return { author: typedAuthor, articles: articleCards };
    }

export const authorProfileQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["author-profile", slug],
    queryFn: () => fetchAuthorProfile(slug),
  });

export default function AuthorProfile() {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, error } = useQuery({
    ...authorProfileQueryOptions(slug || ""),
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <FallbackTitle />
        <Header />
        <main className="container mx-auto px-4 lg:px-8 py-12">
          <Skeleton className="h-40 w-full mb-8" />
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((item) => <Skeleton key={item} className="h-80 w-full" />)}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !data) return <NotFound />;

  const { author, articles } = data;
  const description = author.meta_description || author.bio || `Read garden articles and guides written by ${author.name} on Garden-Review.`;
  const socialLinks = Object.entries(author.social_links || {}).filter(([, url]) => Boolean(url));

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `https://www.garden-review.co.uk/authors/${author.slug}#person`,
    name: author.name,
    url: `https://www.garden-review.co.uk/authors/${author.slug}`,
    jobTitle: author.job_title || author.role || undefined,
    description,
    image: author.avatar_url || undefined,
    knowsAbout: author.expertise && author.expertise.length > 0 ? author.expertise : undefined,
    hasCredential: author.credentials && author.credentials.length > 0
      ? author.credentials.map((c) => ({ "@type": "EducationalOccupationalCredential", name: c }))
      : undefined,
    address: author.location ? { "@type": "PostalAddress", addressLocality: author.location } : undefined,
    worksFor: {
      "@type": "Organization",
      name: "Garden-Review",
      url: "https://www.garden-review.co.uk",
    },
    sameAs: [author.website_url, ...socialLinks.map(([, url]) => url)].filter(Boolean),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        {/* title, description and canonical are emitted server-side by the route head() */}
        <script type="application/ld+json">{jsonLd(personSchema)}</script>
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 lg:px-8 py-12">
        <section className="mb-12 rounded-lg border bg-card p-6 md:p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start">
            {author.avatar_url ? (
              <img src={author.avatar_url} alt={author.name} className="h-28 w-28 rounded-full object-cover" />
            ) : (
              <div className="h-28 w-28 rounded-full bg-muted flex items-center justify-center">
                <User className="h-12 w-12 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2">{author.name}</h1>
              {(author.job_title || author.role) && (
                <p className="text-lg font-medium text-primary mb-3">{author.job_title || author.role}</p>
              )}
              <div className="flex flex-wrap gap-2 mb-4">
                {author.years_experience && (
                  <Badge variant="secondary" className="gap-1">
                    <Award className="h-3 w-3" /> {author.years_experience}+ years experience
                  </Badge>
                )}
                {author.location && (
                  <Badge variant="secondary" className="gap-1">
                    <MapPin className="h-3 w-3" /> {author.location}
                  </Badge>
                )}
                <Badge variant="secondary" className="gap-1">
                  <ShieldCheck className="h-3 w-3" /> Independent reviewer
                </Badge>
              </div>
              {author.bio && <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl">{author.bio}</p>}
              <div className="mt-4 flex flex-wrap gap-3">
                {author.website_url && (
                  <a href={author.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                    <Globe className="h-4 w-4" /> Website
                  </a>
                )}
                {socialLinks.map(([name, url]) => (
                  <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="text-sm capitalize text-primary hover:underline">{name}</a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {((author.credentials?.length ?? 0) + (author.expertise?.length ?? 0) > 0) && (
          <section className="mb-12 grid gap-6 md:grid-cols-2">
            {author.credentials && author.credentials.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="flex items-center gap-2 text-2xl font-bold text-foreground">
                    <Award className="h-5 w-5 text-primary" /> Credentials & Qualifications
                  </h2>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {author.credentials.map((c) => (
                      <li key={c} className="flex items-start gap-2 text-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {author.expertise && author.expertise.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="flex items-center gap-2 text-2xl font-bold text-foreground">
                    <Wrench className="h-5 w-5 text-primary" /> Areas of Expertise
                  </h2>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {author.expertise.map((e) => (
                      <li key={e} className="flex items-start gap-2 text-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </section>
        )}

        {author.testing_methodology && (
          <section className="mb-12">
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-2 text-2xl font-bold text-foreground">
                  <ShieldCheck className="h-5 w-5 text-primary" /> How {author.name.split(" ")[0]} Tests & Reviews
                </h2>
                <p className="text-sm text-muted-foreground">Our hands-on review methodology — built for genuine, trustworthy recommendations.</p>
              </CardHeader>
              <CardContent>
                <p className="text-base leading-relaxed text-foreground whitespace-pre-line">{author.testing_methodology}</p>
              </CardContent>
            </Card>
          </section>
        )}


        <section>
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold text-foreground">Articles by {author.name}</h2>
              <p className="text-muted-foreground">{articles.length} published {articles.length === 1 ? "article" : "articles"}</p>
            </div>
          </div>

          {articles.length === 0 ? (
            <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">No published articles linked yet.</div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((article) => (
                <Link key={article.id} to={`/articles/${article.slug}`}>
                  <Card className="h-full overflow-hidden transition-shadow hover:shadow-nature">
                    {article.featured_image && (
                      <div className="h-48 overflow-hidden bg-card">
                        <img src={article.featured_image} alt={article.featured_image_alt || article.title} className="h-full w-full object-contain transition-transform duration-300 hover:scale-105" />
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <div className="mb-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        {article.category && <Badge variant="secondary">{article.category}</Badge>}
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{article.readingTime}</span>
                      </div>
                      <h3 className="text-xl font-bold leading-tight text-foreground hover:text-primary">{article.title}</h3>
                    </CardHeader>
                    <CardContent>
                      {article.excerpt && <p className="mb-4 line-clamp-3 text-muted-foreground">{article.excerpt}</p>}
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{format(new Date(article.published_at || article.created_at), "MMM d, yyyy")}</span>
                        <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80">Read <ArrowRight className="ml-1 h-3 w-3" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
